/**
 * 阅读 questionFormat 形态校验（IELTS / TOEFL）。
 * 未实现 Runtime 的 format 一律 blocking，避免假 MCQ 进绿区。
 */

import type { ValidationError } from '@examflow/core';

const IELTS_TFNG = 'true_false_not_given' as const;
const IELTS_YNNG = 'yes_no_not_given' as const;

const IELTS_RUNTIME_READY = new Set<string>([
  'multiple_choice',
  IELTS_TFNG,
  IELTS_YNNG,
]);

/** Matching / completion 等尚无独立 Runtime，禁止当普通四选一入库 */
const IELTS_RUNTIME_UNSUPPORTED = new Set<string>([
  'matching_headings',
  'matching_information',
  'matching_features',
  'sentence_completion',
  'summary_completion',
  'diagram_labelling',
  'short_answer',
]);

const TOEFL_RUNTIME_READY = new Set<string>([
  'factual_information',
  'negative_factual',
  'inference',
  'vocabulary',
  'reference',
  'rhetorical_purpose',
]);

const TOEFL_RUNTIME_UNSUPPORTED = new Set<string>([
  'insertion',
  'prose_summary',
]);

function optionContents(question: {
  options?: Array<{ id?: string; content?: string }>;
}): string[] {
  if (!Array.isArray(question.options)) return [];
  return question.options
    .map((o) => String(o.content ?? '').trim().toLowerCase())
    .filter(Boolean);
}

function optionIds(question: {
  options?: Array<{ id?: string; content?: string }>;
}): string[] {
  if (!Array.isArray(question.options)) return [];
  return question.options.map((o) => String(o.id ?? '').trim()).filter(Boolean);
}

function normalizeLabel(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function hasExactOptionSet(contents: string[], expected: string[]): boolean {
  if (contents.length !== expected.length) return false;
  const normalized = contents.map(normalizeLabel).sort();
  const want = expected.map(normalizeLabel).sort();
  return normalized.every((c, i) => c === want[i]);
}

function answerMatchesOptionId(
  correctAnswer: unknown,
  ids: string[]
): boolean {
  const answer = Array.isArray(correctAnswer)
    ? String(correctAnswer[0] ?? '')
    : String(correctAnswer ?? '');
  return ids.includes(answer.trim());
}

function unsupportedFormatError(format: string): ValidationError {
  return {
    field: 'questionFormat',
    message: `题型格式 ${format} 尚无独立作答 Runtime，当前产线禁止入库（请改用已支持的选择题形态）`,
    code: 'QUESTION_FORMAT_RUNTIME_UNSUPPORTED',
    severity: 'blocking',
  };
}

/**
 * IELTS 阅读：按 questionFormat 收紧形态；未实现 format 阻断。
 */
export function validateIeltsReadingQuestionFormat(question: {
  questionFormat?: unknown;
  type?: unknown;
  options?: Array<{ id?: string; content?: string }>;
  correctAnswer?: unknown;
}): ValidationError[] {
  const format =
    typeof question.questionFormat === 'string' ? question.questionFormat.trim() : '';
  if (!format) return [];

  const errors: ValidationError[] = [];

  if (IELTS_RUNTIME_UNSUPPORTED.has(format)) {
    errors.push(unsupportedFormatError(format));
    return errors;
  }

  if (!IELTS_RUNTIME_READY.has(format)) {
    return errors;
  }

  if (format === 'multiple_choice') {
    const count = question.options?.length ?? 0;
    if (count < 4) {
      errors.push({
        field: 'options',
        message: 'IELTS multiple_choice 至少需要 4 个选项',
        code: 'IELTS_MCQ_OPTIONS',
        severity: 'blocking',
      });
    }
    return errors;
  }

  const expected =
    format === IELTS_TFNG
      ? ['true', 'false', 'not given']
      : ['yes', 'no', 'not given'];
  const contents = optionContents(question);
  if (!hasExactOptionSet(contents, expected)) {
    errors.push({
      field: 'options',
      message:
        format === IELTS_TFNG
          ? 'true_false_not_given 选项必须恰好为 True / False / Not Given'
          : 'yes_no_not_given 选项必须恰好为 Yes / No / Not Given',
      code: 'IELTS_JUDGEMENT_OPTIONS',
      severity: 'blocking',
    });
  }

  const ids = optionIds(question);
  if (ids.length > 0 && !answerMatchesOptionId(question.correctAnswer, ids)) {
    errors.push({
      field: 'correctAnswer',
      message: `${format} 的 correctAnswer 必须是选项 ID 之一`,
      code: 'IELTS_JUDGEMENT_ANSWER',
      severity: 'blocking',
    });
  }

  return errors;
}

/**
 * TOEFL 阅读：常规事实/推断等作 MCQ；insertion / prose_summary 暂禁。
 */
export function validateToeflReadingQuestionFormat(question: {
  questionFormat?: unknown;
  options?: Array<{ id?: string; content?: string }>;
}): ValidationError[] {
  const format =
    typeof question.questionFormat === 'string' ? question.questionFormat.trim() : '';
  if (!format) return [];

  if (TOEFL_RUNTIME_UNSUPPORTED.has(format)) {
    return [unsupportedFormatError(format)];
  }

  if (!TOEFL_RUNTIME_READY.has(format)) {
    return [];
  }

  const count = question.options?.length ?? 0;
  if (count < 4) {
    return [
      {
        field: 'options',
        message: `TOEFL ${format} 至少需要 4 个选项`,
        code: 'TOEFL_MCQ_OPTIONS',
        severity: 'blocking',
      },
    ];
  }

  return [];
}
